<template>
  <DialogLayout class-name="google-map-dialog" @close="handleClose">
    <template #title>{{t('common.mapDialog.title')}}</template>

    <template #default>
      <div class="map-dialog-content">
        <!-- 搜索框 -->
        <div class="search-section">
          <el-input ref="searchInputRef" v-model="searchText" :placeholder="t('common.mapDialog.searchPlaceholder')" class="search-input"
            @input="handleSearchInput" @focus="showSuggestions = true">
            <template #suffix>
              <el-icon>
                <Search />
              </el-icon>
            </template>
          </el-input>

          <!-- 地址推荐列表 -->
          <div v-if="showSuggestions && suggestions.length > 0" class="suggestions-list">
            <div v-for="(suggestion, index) in suggestions" :key="index" class="suggestion-item"
              @click="selectSuggestion(suggestion)">
              <el-icon class="location-icon">
                <Location />
              </el-icon>
              <div class="suggestion-content">
                <div class="suggestion-main">{{ suggestion.mainText }}</div>
                <div class="suggestion-secondary">{{ suggestion.secondaryText }}</div>
              </div>
            </div>
          </div>

          <!-- 地址选择提示 -->
          <div v-if="searchText && !showSuggestions" class="address-notice">
            {{ t('common.mapDialog.confirmLocationTip') }}
          </div>
        </div>

        <!-- Google Map -->
        <div class="map-container">
          <GoogleMap 
            :api-key="apiKey" 
            :center="mapCenter" 
            :zoom="zoom" 
            :map-type-control="false"
            :street-view-control="false" 
            :fullscreen-control="false" 
            language="ja" 
            :clickableIcons="false"
            style="width: 100%; height: 100%"
            ref="mapRef">
            <Marker :options="markerOptions" ref="markerRef" />
          </GoogleMap>

          <!-- 地图蒙版 - 搜索框为空时显示 -->
          <div v-if="!searchText.trim()" class="map-overlay">
            <div class="overlay-content">
              <div class="mp-outer">
                <Icon size="md" color="#009E99" name="map" />
              </div>
              <div class="overlay-text">{{ t('common.mapDialog.noAddressSelected') }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <el-button class="dialog-button" @click="handleCancel">
        {{ t('common.cancel') }}
      </el-button>
      <el-button type="primary" class="dialog-button" :disabled="!isAddressSelectedFromSuggestion"
        @click="handleConfirm">
        {{ t('common.confirm') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
/// <reference types="google.maps" />
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Search, Location } from '@element-plus/icons-vue';
import { GoogleMap, Marker } from 'vue3-google-map';
import { useI18n } from 'vue-i18n';
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import type { GoogleMapDialogProps, AddressData } from './google-map-dialog-types';

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  description: string;
}

const props = withDefaults(defineProps<GoogleMapDialogProps>(), {
  initialAddress: '',
  initialLat: 35.6762, // 东京默认坐标
  initialLng: 139.6503
});

const emit = defineEmits<{
  confirm: [data: AddressData];
  cancel: [];
  close: [];
}>();

const { t } = useI18n();

// Google Maps API Key
const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// 搜索文本
const searchText = ref('');

// 搜索输入框引用
const searchInputRef = ref();

// 地图引用
const mapRef = ref();

// 标记引用
const markerRef = ref();

// InfoWindow 实例
let infoWindow: google.maps.InfoWindow | null = null;

// 默认坐标（东京）
const DEFAULT_LAT = 35.6762;
const DEFAULT_LNG = 139.6503;

// 验证并获取有效的初始经纬度
const getValidCoordinate = (value: number | undefined, defaultValue: number): number => {
  if (value === undefined || value === null) return defaultValue;
  const num = typeof value === 'number' ? value : Number(value);
  return !isNaN(num) ? num : defaultValue;
};

// 地图中心点
const mapCenter = ref({
  lat: getValidCoordinate(props.initialLat, DEFAULT_LAT),
  lng: getValidCoordinate(props.initialLng, DEFAULT_LNG)
});

// 地图缩放级别
const zoom = ref(15);

// 选中的地址
const selectedAddress = ref(props.initialAddress);

// 选中的坐标
const selectedLocation = ref({
  lat: getValidCoordinate(props.initialLat, DEFAULT_LAT),
  lng: getValidCoordinate(props.initialLng, DEFAULT_LNG)
});

// 地址推荐列表
const suggestions = ref<Suggestion[]>([]);

// 是否显示推荐列表
const showSuggestions = ref(false);

// 是否从建议列表中选择了地址
const isAddressSelectedFromSuggestion = ref(false);

// AutocompleteService 实例
let autocompleteService: google.maps.places.AutocompleteService | null = null;

// PlacesService 实例
let placesService: google.maps.places.PlacesService | null = null;

// Marker 配置
const markerOptions = computed(() => ({
  position: selectedLocation.value,
  draggable: false,
}));

// 更新位置
const updateLocation = async (lat: number, lng: number) => {
  selectedLocation.value = { lat, lng };
  mapCenter.value = { lat, lng };

  // 使用 Geocoding API 获取地址
  await geocodeLocation(lat, lng);

  // 更新 InfoWindow
  updateInfoWindow();
};

// 解析地址组件
const parseAddressComponents = (addressComponents: google.maps.GeocoderAddressComponent[]) => {
  const components = {
    province: '',
    city: '',
    district: '',
    street: '',
  };

  addressComponents.forEach((component) => {
    const types = component.types;

    // 都道府県 (administrative_area_level_1)
    if (types.includes('administrative_area_level_1')) {
      components.province = component.long_name;
    }
    // 市区町村 (locality or administrative_area_level_2)
    else if (types.includes('locality')) {
      components.city = component.long_name;
    }
    else if (types.includes('administrative_area_level_2') && !components.city) {
      components.city = component.long_name;
    }
    // 区 (sublocality or administrative_area_level_3)
    else if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
      components.district = component.long_name;
    }
    else if (types.includes('administrative_area_level_3') && !components.district) {
      components.district = component.long_name;
    }
    // 街道/丁目 (route or premise)
    else if (types.includes('route') || types.includes('premise')) {
      components.street = component.long_name;
    }
  });

  return components;
};

// 存储当前地址组件
const currentAddressComponents = ref<any>(null);

// 地理编码：将经纬度转换为地址
const geocodeLocation = async (lat: number, lng: number) => {
  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({
      location: { lat, lng },
      language: 'ja', // 指定返回日文地址
    });

    if (result.results && result.results[0]) {
      selectedAddress.value = result.results[0].formatted_address;
      // 保存解析后的地址组件
      currentAddressComponents.value = parseAddressComponents(result.results[0].address_components);
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
};

// 处理搜索输入
let searchTimer: number | null = null;
const handleSearchInput = () => {
  // 清除之前的定时器
  if (searchTimer) {
    clearTimeout(searchTimer);
  }

  // 如果输入为空，清空建议列表和选中的位置
  if (!searchText.value.trim()) {
    suggestions.value = [];
    showSuggestions.value = false;
    isAddressSelectedFromSuggestion.value = false; // 重置选择标记
    // 清空选中的位置和地址
    selectedLocation.value = {
      lat: getValidCoordinate(props.initialLat, DEFAULT_LAT),
      lng: getValidCoordinate(props.initialLng, DEFAULT_LNG)
    };
    selectedAddress.value = '';
    currentAddressComponents.value = null;
    // 重置地图中心
    mapCenter.value = {
      lat: getValidCoordinate(props.initialLat, DEFAULT_LAT),
      lng: getValidCoordinate(props.initialLng, DEFAULT_LNG)
    };
    return;
  }

  // 延迟300ms执行搜索，避免频繁调用API
  searchTimer = setTimeout(() => {
    fetchSuggestions();
  }, 300) as unknown as number;
};

// 获取地址推荐
const fetchSuggestions = () => {
  if (!autocompleteService || !searchText.value.trim()) return;

  autocompleteService.getPlacePredictions(
    {
      input: searchText.value,
      componentRestrictions: { country: 'jp' }, // 限制为日本
      language: 'ja', // 指定返回日文
    },
    (predictions, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
        suggestions.value = predictions.map((prediction) => ({
          placeId: prediction.place_id,
          mainText: prediction.structured_formatting.main_text,
          secondaryText: prediction.structured_formatting.secondary_text || '',
          description: prediction.description,
        }));
        showSuggestions.value = true;
      } else {
        suggestions.value = [];
      }
    }
  );
};

// 选择推荐地址
const selectSuggestion = async (suggestion: Suggestion) => {
  if (!placesService) return;

  showSuggestions.value = false;
  searchText.value = suggestion.description;

  // 获取地点详情
  placesService.getDetails(
    {
      placeId: suggestion.placeId,
      fields: ['geometry', 'formatted_address', 'address_components'],
    },
    (place, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        // 直接更新位置和地图中心，不进行反向地理编码
        selectedLocation.value = { lat, lng };
        mapCenter.value = { lat, lng };

        // 使用建议列表中的 description 作为选中的地址
        selectedAddress.value = suggestion.description;

        // 解析地址组件
        if (place.address_components) {
          currentAddressComponents.value = parseAddressComponents(place.address_components);
        }

        // 标记为已从建议列表中选择
        isAddressSelectedFromSuggestion.value = true;

        // 更新 InfoWindow
        updateInfoWindow();
      }
    }
  );
};

// 处理取消
const handleCancel = () => {
  emit('cancel');
  emit('close');
};

// 处理确认
const handleConfirm = () => {
  const addressData: AddressData = {
    formattedAddress: selectedAddress.value,
    lat: selectedLocation.value.lat,
    lng: selectedLocation.value.lng,
    addressComponents: currentAddressComponents.value,
  };

  emit('confirm', addressData);
  emit('close');
};

// 处理关闭
const handleClose = () => {
  emit('close');
};

// 更新 InfoWindow
const updateInfoWindow = () => {
  if (!mapRef.value?.map || !markerRef.value?.marker) return;

  // 创建或更新 InfoWindow
  if (!infoWindow) {
    infoWindow = new google.maps.InfoWindow({
      disableAutoPan: true, // 禁用自动平移
    });
  }

  const content = searchText.value || t('common.mapDialog.noAddressSelected');
  infoWindow.setContent(`<div style="padding: 4px 8px; font-size: 14px; color: #495057;">${content}</div>`);
  infoWindow.open({
    map: mapRef.value.map,
    anchor: markerRef.value.marker,
    shouldFocus: false, // 禁用焦点
  });
};

// 点击外部关闭推荐列表
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const searchSection = target.closest('.search-section');
  if (!searchSection) {
    showSuggestions.value = false;
  }
};

// 等待 Google Maps API 加载
const waitForGoogleMapsAPI = (): Promise<void> => {
  return new Promise((resolve) => {
    const checkGoogle = () => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        resolve();
      } else {
        setTimeout(checkGoogle, 100);
      }
    };
    checkGoogle();
  });
};

// 初始化时如果有地址，进行地理编码
onMounted(async () => {
  // 等待 Google Maps API 加载完成
  await waitForGoogleMapsAPI();

  // 初始化 Autocomplete Service
  autocompleteService = new google.maps.places.AutocompleteService();

  // 创建一个隐藏的div用于初始化PlacesService
  const div = document.createElement('div');
  placesService = new google.maps.places.PlacesService(div);

  // 添加全局点击事件监听
  document.addEventListener('click', handleClickOutside);

  const hasValidLat = props.initialLat !== undefined && props.initialLat !== null && typeof props.initialLat === 'number';
  const hasValidLng = props.initialLng !== undefined && props.initialLng !== null && typeof props.initialLng === 'number';

  if (hasValidLat && hasValidLng) {
    // 如果有初始经纬度，设置搜索框文本并反向地理编码
    if (props.initialAddress) {
      searchText.value = props.initialAddress;
    }
    await geocodeLocation(props.initialLat, props.initialLng);
    // 如果有初始经纬度，标记为已选择（表示之前已经选择过）
    isAddressSelectedFromSuggestion.value = true;
  } else if (props.initialAddress) {
    // 只有地址没有经纬度，填充搜索框但不标记为已选择
    searchText.value = props.initialAddress;
    // 用户需要从建议中重新选择
  }

  // 等待地图加载完成后显示 InfoWindow
  // 使用多次尝试确保地图和标记都已加载
  const tryShowInfoWindow = (attempts = 0) => {
    if (attempts > 10) return; // 最多尝试10次

    if (mapRef.value?.map && markerRef.value?.marker) {
      updateInfoWindow();
    } else {
      setTimeout(() => tryShowInfoWindow(attempts + 1), 200);
    }
  };

  tryShowInfoWindow();
});

// 组件卸载时清理
onUnmounted(() => {
  if (searchTimer) {
    clearTimeout(searchTimer);
  }
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss">
// 全局样式：隐藏 InfoWindow 关闭按钮
.gm-style-iw-chr {
  display: none !important;
}

// 调整 InfoWindow 内边距
.gm-style .gm-style-iw-c {
  padding: 0 !important;
}

.gm-style .gm-style-iw-d {
  overflow: hidden !important;
}
</style>

<style lang="scss" scoped>
.google-map-dialog {
  .map-dialog-content {
    display: flex;
    flex-direction: column;
  }

  .mp-outer {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #DBF1F1;
    text-align: center;
    line-height: 75px;
  }

  .search-section {
    width: 100%;
    position: relative;

    .search-input {
      width: 100%;
    }

    .address-notice {
      margin-top: 8px;
      padding: 8px 12px;
      background: #FEF3E2;
      border-left: 3px solid #F59E0B;
      border-radius: 4px;
      font-size: 12px;
      color: #92400E;
      line-height: 18px;
    }

    .suggestions-list {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 4px;
      background: #fff;
      border: 1px solid #E5E7EB;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      max-height: 300px;
      overflow-y: auto;
      z-index: 1000;

      .suggestion-item {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        padding: 12px 16px;
        cursor: pointer;
        transition: background-color 0.2s;
        margin-bottom: 4px;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background-color: #F4EFFD;
        }

        .location-icon {
          font-size: 20px;
          color: #6B7280;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .suggestion-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: row;
          margin-top: 3px;
          line-height: 17px;

          .suggestion-main {
            font-size: 14px;
            color: #495057;
            font-weight: 500;
          }

          .suggestion-secondary {
            font-size: 14px;
            color: #88929B;
            margin-left: 4px;
          }
        }
      }
    }
  }

  .map-container {
    width: 528px;
    height: 400px;
    border: 1px solid #E5E7EB;
    overflow: hidden;
    margin-top: 24px;
    position: relative;

    .map-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.75);
      backdrop-filter: blur(2px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;

      .overlay-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;

        .overlay-icon {
          color: #D1D5DB;
          font-size: 48px;
        }

        .overlay-text {
          font-size: 16px;
          color: #6B7280;
          font-weight: 500;
          text-align: center;
        }
      }
    }
  }

  .address-hint {
    padding: 12px;
    background: #F7F8FA;
    border-radius: 4px;
    font-size: 14px;
    color: #212429;
    line-height: 22px;
  }

  .dialog-button {
    min-width: 72px;
  }
}
</style>
